/*
 * Create the document's shared service and cleanup boundary for browser UI.
 * The application shell and DOM fixtures create one context and pass it to components and deep
 * helpers. Services remain bound to that document through asynchronous work; activity counters
 * let refresh policy observe open writing surfaces. Disposal aborts scoped reads, releases every
 * registered resource and waits for explicitly tracked operations.
 *
 * Example: const context = createUIContext({ document, window });
 *          const releaseWriting = context.services.activity.begin('writing');
 *          releaseWriting();
 *          await context.dispose();
 */

import { createStore } from './store.js';

export function createActivity() {
  const state = createStore({ writing: 0, modal: 0 });
  return {
    get: state.get,
    subscribe: state.subscribe,

    begin(kind) {
      let held = true;
      state.update((current) => ({ ...current, [kind]: (current[kind] || 0) + 1 }));
      return () => {
        if (!held) return;
        held = false;
        state.update((current) => ({ ...current, [kind]: Math.max(0, current[kind] - 1) }));
      };
    }
  };
}

export function createUIContext({ document, window, services = {}, fetch, onWork, onError } = {}) {
  if (!document || !window) throw new Error('A UI context requires its document and window.');

  const controller = new AbortController();
  const cleanups = new Set(),
    pending = new Set(),
    instances = new Map();
  let closing,
    disposed = false;

  const context = {
    document,
    window,
    fetch: fetch || window.fetch?.bind(window),
    signal: controller.signal,
    services: { ...services, activity: services.activity || createActivity() },
    onError,
    onWork,

    recordWork(kind, key) {
      context.onWork?.(kind, key);
    },

    service(factory) {
      if (controller.signal.aborted) throw new Error('The UI context has been disposed.');
      if (!instances.has(factory)) instances.set(factory, factory(context));
      return instances.get(factory);
    },

    state(factory) {
      return context.service(factory);
    },

    onDispose(cleanup) {
      context.own(cleanup);
      return cleanup;
    },

    own(cleanup) {
      if (disposed) throw new Error('The UI context has been disposed.');
      cleanups.add(cleanup);
      return () => cleanups.delete(cleanup);
    },

    listen(target, name, listener, options) {
      target.addEventListener(name, listener, options);
      const release = () => target.removeEventListener(name, listener, options);
      const forget = context.own(release);
      return () => {
        forget();
        release();
      };
    },

    track(promise) {
      pending.add(promise);
      promise.then(
        () => pending.delete(promise),
        () => pending.delete(promise)
      );
      return promise;
    },

    dispose() {
      if (closing) return closing;
      let resolveClosing, rejectClosing;
      closing = new Promise((resolve, reject) => {
        resolveClosing = resolve;
        rejectClosing = reject;
      });
      controller.abort();
      void (async () => {
        const errors = [];
        while (cleanups.size || pending.size) {
          for (const cleanup of [...cleanups].reverse()) {
            cleanups.delete(cleanup);
            try {
              await cleanup();
            } catch (error) {
              errors.push(error);
            }
          }
          if (pending.size) await Promise.allSettled([...pending]);
        }
        disposed = true;
        instances.clear();
        if (errors.length) throw new AggregateError(errors, 'UI context cleanup failed.');
      })().then(resolveClosing, rejectClosing);
      return closing;
    }
  };

  return context;
}
